import { useEffect, useRef, useState } from "react";

import {
  ACTIVITY_LABELS,
  ask,
  logTime,
  searchCases,
  type ActivityType,
  type AskResponse,
  type SiggeCase,
} from "./api";
import { signInWithOffice, tokenStore, type SiggeUser } from "./auth";
import {
  formatDuration,
  formatTime,
  loadTimer,
  newTimer,
  removeSuspected,
  runningDurationMs,
  saveTimer,
  stopTimer,
  tickHeartbeat,
  toggleSegment,
  totalIncludedMinutes,
  TIMER_CONSTANTS,
  type TimerState,
} from "./timer";
import {
  loadSettings,
  saveSettings,
  type AddinSettings,
} from "./settings";

type Status =
  | { kind: "loading" }
  | { kind: "signin" }
  | { kind: "ready"; user: SiggeUser }
  | { kind: "error"; message: string };

export function App() {
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    void initialise(setStatus);
  }, []);

  if (status.kind === "loading") return <Centered>Laddar…</Centered>;
  if (status.kind === "error") {
    return (
      <div className="pane">
        <div className="error-box">{status.message}</div>
        <button className="btn btn-primary" onClick={() => initialise(setStatus)}>
          Försök igen
        </button>
      </div>
    );
  }
  if (status.kind === "signin") {
    return (
      <div className="pane">
        <h1>Sigge för Word</h1>
        <p className="muted">Logga in med ditt 365-konto.</p>
        <button
          className="btn btn-primary"
          onClick={() => initialise(setStatus, { force: true })}
        >
          Logga in via Microsoft
        </button>
      </div>
    );
  }

  return (
    <ReadyPane
      user={status.user}
      onSignOut={() => {
        tokenStore.clear();
        setStatus({ kind: "signin" });
      }}
    />
  );
}

async function initialise(
  setStatus: (s: Status) => void,
  { force = false }: { force?: boolean } = {}
): Promise<void> {
  setStatus({ kind: "loading" });
  try {
    if (!tokenStore.access || force) {
      const result = await signInWithOffice();
      setStatus({ kind: "ready", user: result.user });
      return;
    }
    setStatus({
      kind: "ready",
      user: {
        id: 0,
        email: Office.context.document?.url || "",
        full_name: "",
        role: "lawyer",
      },
    });
  } catch (err) {
    const msg = (err as Error).message || String(err);
    if (msg.toLowerCase().includes("inte inloggad")) {
      setStatus({ kind: "signin" });
    } else {
      setStatus({ kind: "error", message: msg });
    }
  }
}

type Tab = "ask" | "time" | "timer" | "clauselib" | "settings";

function ReadyPane({
  user,
  onSignOut,
}: {
  user: SiggeUser;
  onSignOut: () => void;
}) {
  const [tab, setTab] = useState<Tab>("ask");
  const [caseQuery, setCaseQuery] = useState("");
  const [cases, setCases] = useState<SiggeCase[]>([]);
  const [selected, setSelected] = useState<SiggeCase | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [settings, setSettings] = useState<AddinSettings>(loadSettings);
  const [ctrlSPromptCase, setCtrlSPromptCase] = useState<SiggeCase | null>(
    null
  );
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => runSearch(caseQuery), 250);
  }, [caseQuery]);

  const runSearch = async (q: string) => {
    try {
      const res = await searchCases(q);
      setCases(res.slice(0, 8));
      setSearchError(null);
    } catch (err) {
      setSearchError((err as Error).message);
    }
  };

  // Word-hook: pollar document.saved var 3:e sekund. När flaggan
  // flippar från false → true har juristen just tryckt Ctrl+S (eller
  // auto-save körts). Vi öppnar då Logga tid-prompt om setting on
  // och ett ärende är valt. Ren polling — Office.js exponerar ingen
  // "document saved"-event i taskpanen, och Ctrl+S-tangenten
  // fångas inte upp eftersom keyboard-fokus normalt ligger i doc:et,
  // inte i taskpane-iframen.
  const prevSaved = useRef<boolean | null>(null);
  useEffect(() => {
    if (!settings.ctrlSPrompt) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        // Word.run kräver Word host. Om vi råkar bygga för Outlook
        // (vi ska inte, men fail-safe:t) — ignorera.
        if (typeof Word === "undefined") return;
        await Word.run(async (context) => {
          const doc = context.document;
          doc.load("saved");
          await context.sync();
          const savedNow = !!(doc as unknown as { saved?: boolean }).saved;
          if (prevSaved.current === false && savedNow && selected) {
            setCtrlSPromptCase(selected);
          }
          prevSaved.current = savedNow;
        });
      } catch {
        /* ignorera transient Word.run-fel */
      }
    };
    const interval = window.setInterval(() => void poll(), 3_000);
    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [settings.ctrlSPrompt, selected]);

  return (
    <div className="pane">
      <header className="pane-head">
        <div>
          <div className="muted small">Inloggad som</div>
          <div>{user.email}</div>
        </div>
        <button className="btn-link" onClick={onSignOut}>
          Logga ut
        </button>
      </header>

      {/* Case picker är GEMENSAM för alla tabs — juristen väljer ärende
          en gång och slipper göra om det för att växla mellan fråga och
          tidsloggning. Selection följer med när tab byts. */}
      <div className="field">
        <label className="field-label">Välj ärende</label>
        <input
          type="text"
          value={caseQuery}
          onChange={(e) => setCaseQuery(e.target.value)}
          placeholder="Sök ärende…"
        />
      </div>

      <ul className="case-list">
        {cases.map((c) => (
          <li key={c.id}>
            <button
              className={selected?.id === c.id ? "case-row is-on" : "case-row"}
              onClick={() => setSelected(c)}
            >
              <div className="case-title">{c.title}</div>
              <div className="muted small">
                #{c.id} · {c.client?.full_name || c.client?.email || "—"}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {searchError && <div className="error-box">{searchError}</div>}

      <div className="tab-bar">
        <button
          className={tab === "ask" ? "tab is-on" : "tab"}
          onClick={() => setTab("ask")}
        >
          Fråga
        </button>
        <button
          className={tab === "time" ? "tab is-on" : "tab"}
          onClick={() => setTab("time")}
        >
          Logga tid
        </button>
        {settings.timerEnabled && (
          <button
            className={tab === "timer" ? "tab is-on" : "tab"}
            onClick={() => setTab("timer")}
          >
            Timer
          </button>
        )}
        <button
          className={tab === "settings" ? "tab is-on" : "tab"}
          onClick={() => setTab("settings")}
        >
          Inställningar
        </button>
      </div>

      {tab === "ask" && <AskTab selected={selected} />}
      {tab === "time" && <TimeTab selected={selected} />}
      {tab === "timer" && settings.timerEnabled && (
        <TimerTab
          selected={selected}
          autoStart={settings.autoStartOnFocus}
        />
      )}
      {tab === "settings" && (
        <SettingsTab
          settings={settings}
          onChange={(next) => {
            setSettings(next);
            saveSettings(next);
          }}
        />
      )}

      {ctrlSPromptCase && (
        <SaveTimePrompt
          caseTitle={ctrlSPromptCase.title}
          onLogTime={() => {
            setCtrlSPromptCase(null);
            setSelected(ctrlSPromptCase);
            setTab("time");
          }}
          onDismiss={() => setCtrlSPromptCase(null)}
        />
      )}
    </div>
  );
}

function SaveTimePrompt({
  caseTitle,
  onLogTime,
  onDismiss,
}: {
  caseTitle: string;
  onLogTime: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="prompt-overlay" onClick={onDismiss}>
      <div className="prompt-card" onClick={(e) => e.stopPropagation()}>
        <h3>Logga tid?</h3>
        <p className="muted">
          Du sparade nyss dokumentet. Vill du logga tid på ärendet{" "}
          <strong>{caseTitle}</strong> nu?
        </p>
        <div className="prompt-actions">
          <button className="btn btn-ghost btn-sm" onClick={onDismiss}>
            Inte nu
          </button>
          <button className="btn btn-primary btn-sm" onClick={onLogTime}>
            Öppna Logga tid
          </button>
        </div>
      </div>
    </div>
  );
}

function AskTab({ selected }: { selected: SiggeCase | null }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<AskResponse | null>(null);
  const [includeSelection, setIncludeSelection] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!selected || !question.trim()) return;
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      let docContext = "";
      if (includeSelection) {
        docContext = await getDocumentExcerpt();
      }
      const fullQuestion = docContext
        ? `${question.trim()}\n\nKontext från Word-dokumentet:\n${docContext}`
        : question.trim();
      const res = await ask(selected.id, fullQuestion);
      setAnswer(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const insertAnswer = async () => {
    if (!answer) return;
    try {
      await Word.run(async (context) => {
        const range = context.document.getSelection();
        range.insertText(answer.answer, Word.InsertLocation.replace);
        await context.sync();
      });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="tab-pane">
      <div className="field">
        <label className="field-label">Fråga</label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder='Exempel: "passar denna klausul med vad ärendet säger om uppsägning?"'
          rows={3}
          disabled={busy}
        />
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={includeSelection}
          onChange={(e) => setIncludeSelection(e.target.checked)}
          disabled={busy}
        />
        Inkludera markerad text från dokumentet
      </label>

      {error && <div className="error-box">{error}</div>}

      <button
        className="btn btn-primary btn-block"
        onClick={send}
        disabled={!selected || !question.trim() || busy}
      >
        {busy ? "Frågar…" : "Fråga Sigge"}
      </button>

      {answer && (
        <div className="answer-box">
          <div className="answer-text">{answer.answer}</div>
          <button className="btn btn-ghost btn-block" onClick={insertAnswer}>
            Infoga svar i dokumentet
          </button>
          {answer.sources.length > 0 && (
            <details>
              <summary>Källor ({answer.sources.length})</summary>
              <ul className="src-list">
                {answer.sources.map((s) => (
                  <li key={s.n}>
                    <strong>[{s.n}]</strong> {s.document_title}
                    {s.page !== null && ` · sid ${s.page}`}
                    <div className="muted small">{s.text.slice(0, 200)}…</div>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="muted small">
            {answer.model} · {answer.input_tokens} in / {answer.output_tokens} ut
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tidsloggning — samma flow som Outlook-add-in:et:
 *   aktivitet + minuter + anteckning + debiterbart → POST /api/time-entries/
 *
 * Använder ärendet valt i den delade case-pickern ovanför tabsen.
 * Success-vy klarar reset av fälten så juristen kan logga fler poster i följd.
 */
function TimeTab({ selected }: { selected: SiggeCase | null }) {
  const [activity, setActivity] = useState<ActivityType>("dokgranskning");
  const [minutes, setMinutes] = useState<string>("15");
  const [note, setNote] = useState<string>("");
  const [billable, setBillable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logged, setLogged] = useState<{
    minutes: number;
    title: string;
  } | null>(null);

  const submit = async () => {
    if (!selected) {
      setError("Välj ett ärende först.");
      return;
    }
    const mins = parseInt(minutes, 10);
    if (Number.isNaN(mins) || mins <= 0) {
      setError("Ange minuter (> 0).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await logTime({
        case: selected.id,
        activity_type: activity,
        duration_minutes: mins,
        note,
        billable,
      });
      setLogged({ minutes: mins, title: selected.title });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (logged) {
    return (
      <div className="tab-pane">
        <div className="success-box">
          ✓ {logged.minutes} min loggat på <strong>{logged.title}</strong>.
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setLogged(null);
            setNote("");
            setMinutes("15");
          }}
        >
          Logga mer tid
        </button>
      </div>
    );
  }

  return (
    <div className="tab-pane">
      <div className="field">
        <label className="field-label">Aktivitet</label>
        <select
          value={activity}
          onChange={(e) => setActivity(e.target.value as ActivityType)}
          disabled={busy}
        >
          {(Object.keys(ACTIVITY_LABELS) as ActivityType[]).map((k) => (
            <option key={k} value={k}>
              {ACTIVITY_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Minuter</label>
        <div className="minutes-row">
          <input
            type="number"
            min={1}
            max={1440}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            disabled={busy}
          />
          <div className="quick-mins">
            {[15, 30, 60].map((m) => (
              <button
                key={m}
                className="chip"
                onClick={() => setMinutes(String(m))}
                disabled={busy}
              >
                {m} min
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="field">
        <label className="field-label">Anteckning</label>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="T.ex. Granskning av avtalsförslag"
          disabled={busy}
        />
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={billable}
          onChange={(e) => setBillable(e.target.checked)}
          disabled={busy}
        />
        Debiterbart
      </label>

      {error && <div className="error-box">{error}</div>}

      <button
        className="btn btn-primary btn-block"
        onClick={submit}
        disabled={busy || !selected}
      >
        {busy ? "Loggar…" : "Logga tid"}
      </button>
    </div>
  );
}

/**
 * Timer-tab med heartbeat-baserad segment-detektering och en
 * "Review activity"-vy där juristen kan checka/uncheka enskilda
 * segment innan hen loggar tid.
 *
 * Kontrakt med resten av koden:
 *   * Använder samma delade case-picker som övriga tabs — timern
 *     får `selected` som prop.
 *   * State persisteras i localStorage så att refresh/reopen
 *     återställer pågående session.
 *   * autoStart:t startar timern EN gång per taskpane-mount om
 *     inget redan pågår och ett ärende är valt.
 */
function TimerTab({
  selected,
  autoStart,
}: {
  selected: SiggeCase | null;
  autoStart: boolean;
}) {
  const [state, setState] = useState<TimerState | null>(loadTimer);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0); // för att uppdatera duration-display
  const autoStartedRef = useRef(false);

  // Heartbeat + display-tick. Uppdaterar state var 15:e sek när
  // isRunning (för segment-detektering) och triggar re-render var
  // sekund för att visa löpande tid.
  useEffect(() => {
    if (!state?.isRunning) return;
    const heartbeat = window.setInterval(() => {
      setState((prev) => {
        if (!prev || !prev.isRunning) return prev;
        const next = tickHeartbeat(prev);
        saveTimer(next);
        return next;
      });
    }, TIMER_CONSTANTS.HEARTBEAT_MS);
    const display = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => {
      window.clearInterval(heartbeat);
      window.clearInterval(display);
    };
  }, [state?.isRunning]);

  // Visibility-hook: när fliken kommer tillbaka från hidden triggar
  // vi en synkron heartbeat-tick för att stänga eventuellt gap direkt.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      setState((prev) => {
        if (!prev || !prev.isRunning) return prev;
        const next = tickHeartbeat(prev);
        saveTimer(next);
        return next;
      });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Auto-start om setting on, ett ärende är valt och ingen timer finns.
  // Körs BARA en gång per komponent-mount för att undvika loop.
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!autoStart) return;
    if (state) return;
    if (!selected) return;
    autoStartedRef.current = true;
    const t = newTimer(selected.id, selected.title);
    saveTimer(t);
    setState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, autoStart]);

  const start = () => {
    if (!selected) {
      setError("Välj ett ärende först.");
      return;
    }
    const t = newTimer(selected.id, selected.title);
    saveTimer(t);
    setState(t);
    setReviewOpen(false);
    setError(null);
  };

  const stop = () => {
    setState((prev) => {
      if (!prev) return prev;
      const next = stopTimer(prev);
      saveTimer(next);
      return next;
    });
    setReviewOpen(true);
  };

  const logAndClose = async () => {
    if (!state) return;
    const total = totalIncludedMinutes(state);
    if (total <= 0) {
      setError("Ingen incheckad tid att logga.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await logTime({
        case: state.caseId!,
        activity_type: "dokgranskning",
        duration_minutes: total,
        note: state.note,
        billable: state.billable,
      });
      saveTimer(null);
      setState(null);
      setReviewOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const disregard = () => {
    if (!state) return;
    if (state.segments.length > 0 || state.isRunning) {
      if (!window.confirm("Ta bort denna timer helt? Ingen tid loggas.")) {
        return;
      }
    }
    saveTimer(null);
    setState(null);
    setReviewOpen(false);
  };

  const suspectedCount = state?.segments.filter(
    (s) => s.suspected && !s.included
  ).length ?? 0;

  // tick behövs för att react ska rerendera under körning (löpande timer)
  void tick;

  return (
    <div className="tab-pane">
      {state?.caseTitle && (
        <div className="muted small">
          Ärende: <strong>{state.caseTitle}</strong> · #{state.caseId}
        </div>
      )}

      <div className="timer-display">
        {formatDuration(state ? runningDurationMs(state) : 0)}
      </div>

      <div className="timer-actions">
        {state?.isRunning ? (
          <button className="btn btn-primary btn-block" onClick={stop}>
            Stoppa
          </button>
        ) : state ? (
          <button className="btn btn-primary btn-block" onClick={start}>
            Starta ny
          </button>
        ) : (
          <button
            className="btn btn-primary btn-block"
            onClick={start}
            disabled={!selected}
          >
            Starta
          </button>
        )}
      </div>

      {state && state.segments.length > 0 && (
        <details
          className="review-panel"
          open={reviewOpen}
          onToggle={(e) => setReviewOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary>
            Granska aktivitet
            {suspectedCount > 0 && (
              <span className="badge badge-warn">
                {suspectedCount} misstänkt{suspectedCount === 1 ? "" : "a"}
              </span>
            )}
          </summary>

          <ul className="segment-list">
            {state.segments.map((seg) => {
              const durMin = Math.round(
                (new Date(seg.endISO).getTime() -
                  new Date(seg.startISO).getTime()) /
                  60_000
              );
              return (
                <li
                  key={seg.id}
                  className={
                    seg.suspected
                      ? "segment-row is-suspected"
                      : "segment-row"
                  }
                >
                  <label className="segment-check">
                    <input
                      type="checkbox"
                      checked={seg.included}
                      onChange={() =>
                        setState((prev) => {
                          if (!prev) return prev;
                          const next = toggleSegment(prev, seg.id);
                          saveTimer(next);
                          return next;
                        })
                      }
                    />
                  </label>
                  <div className="segment-time">
                    {formatTime(seg.startISO)}–{formatTime(seg.endISO)}
                  </div>
                  <div className="segment-dur">{durMin} min</div>
                  <div className="segment-tag">
                    {seg.suspected ? "Misstänkt" : "Aktiv"}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="review-controls">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() =>
                setState((prev) => {
                  if (!prev) return prev;
                  const next = removeSuspected(prev);
                  saveTimer(next);
                  return next;
                })
              }
              disabled={suspectedCount === 0}
            >
              Avmarkera misstänkta
            </button>
            <div className="review-total">
              Totalt: <strong>{totalIncludedMinutes(state)} min</strong>
            </div>
          </div>

          <div className="field">
            <label className="field-label">Anteckning</label>
            <textarea
              rows={2}
              value={state.note}
              onChange={(e) =>
                setState((prev) => {
                  if (!prev) return prev;
                  const next = { ...prev, note: e.target.value };
                  saveTimer(next);
                  return next;
                })
              }
              placeholder="T.ex. Genomgång av avtalsförslag"
            />
          </div>

          <label className="toggle">
            <input
              type="checkbox"
              checked={state.billable}
              onChange={(e) =>
                setState((prev) => {
                  if (!prev) return prev;
                  const next = { ...prev, billable: e.target.checked };
                  saveTimer(next);
                  return next;
                })
              }
            />
            Debiterbart
          </label>
        </details>
      )}

      {error && <div className="error-box">{error}</div>}

      {state && !state.isRunning && state.segments.length > 0 && (
        <div className="timer-final-actions">
          <button
            className="btn btn-primary btn-block"
            onClick={() => void logAndClose()}
            disabled={busy || totalIncludedMinutes(state) === 0}
          >
            {busy
              ? "Loggar…"
              : `Logga tid (${totalIncludedMinutes(state)} min)`}
          </button>
          <button
            className="btn btn-ghost btn-block"
            onClick={disregard}
            disabled={busy}
          >
            Ignorera
          </button>
        </div>
      )}

      {!state && (
        <p className="muted small">
          Tryck Starta för att börja spåra tid. Timern räknar bara ner
          intervall där vi tekniskt förlorade spårningen (browser-sömn,
          minskad prioritet av OS m.m.) som misstänkta — inaktivitet i
          tangentbord/mus tolkas ALDRIG som paus.
        </p>
      )}
    </div>
  );
}

/**
 * Per-enhet-inställningar för add-in:et. Sparas i localStorage så
 * varje maskin/browser har sin egen prefererade config. Ändringar
 * ropar uppåt via `onChange` så parent hinner spara + kunna reagera
 * på t.ex. Ctrl+S-toggle.
 */
function SettingsTab({
  settings,
  onChange,
}: {
  settings: AddinSettings;
  onChange: (next: AddinSettings) => void;
}) {
  return (
    <div className="tab-pane">
      <label className="settings-row">
        <input
          type="checkbox"
          checked={settings.timerEnabled}
          onChange={(e) =>
            onChange({ ...settings, timerEnabled: e.target.checked })
          }
        />
        <div>
          <div className="settings-label">Aktivera Timer-fliken</div>
          <div className="muted small">
            Tidsspårning med start/stopp och granskning innan loggning.
          </div>
        </div>
      </label>

      <label className="settings-row">
        <input
          type="checkbox"
          checked={settings.ctrlSPrompt}
          onChange={(e) =>
            onChange({ ...settings, ctrlSPrompt: e.target.checked })
          }
        />
        <div>
          <div className="settings-label">
            Fråga om tidsloggning när jag sparar (Ctrl+S)
          </div>
          <div className="muted small">
            Öppnar Logga tid-fliken automatiskt när du sparar dokumentet.
          </div>
        </div>
      </label>

      <label className="settings-row">
        <input
          type="checkbox"
          checked={settings.autoStartOnFocus}
          onChange={(e) =>
            onChange({ ...settings, autoStartOnFocus: e.target.checked })
          }
        />
        <div>
          <div className="settings-label">
            Starta timer automatiskt när jag öppnar taskpanen
          </div>
          <div className="muted small">
            Kräver att Timer-fliken är på och ett ärende är valt.
          </div>
        </div>
      </label>
    </div>
  );
}

/**
 * Placeholder för framtida klausul-bibliotek. Innehållet + endpointen
 * är inte byggda än (juristen ska kunna slå upp godkända standardklausuler
 * från byråns egen bank och infoga i Word-dokumentet). Vi visar en
 * tydlig "kommer snart"-vy så tabsen är fullständigt reserverad och
 * användaren inte tror att den är trasig.
 */
// ClauseLibTab borttagen — var en "Kommer snart"-placeholder som tog
// upp en tab-slot utan funktionalitet. Feature-request-flow via
// support@sigge.io tills feature:n existerar.

/**
 * Plockar markerad text från Word, eller — om inget är markerat — de
 * första ~2000 tecknen av dokumentet som kontext.
 */
function getDocumentExcerpt(): Promise<string> {
  return new Promise((resolve) => {
    try {
      Word.run(async (context) => {
        const sel = context.document.getSelection();
        sel.load("text");
        await context.sync();
        if (sel.text && sel.text.trim().length > 0) {
          resolve(sel.text.trim().slice(0, 4000));
          return;
        }
        const body = context.document.body;
        body.load("text");
        await context.sync();
        resolve((body.text || "").trim().slice(0, 2000));
      }).catch(() => resolve(""));
    } catch {
      resolve("");
    }
  });
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="centered">{children}</div>;
}
